import { useState } from 'react'
import type { RealityEntry, RealityStore } from '../engine/types'
import { useFinanceCopy } from '../CopyContext'

const STORAGE_KEY = '312-finance-reality'

function loadStore(): RealityStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw) as RealityStore
  } catch {
    /* ignore parse errors */
  }
  return { entries: [], lastUpdated: '' }
}

function saveStore(store: RealityStore): void {
  const updated = { ...store, lastUpdated: new Date().toISOString() }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
}

function blankEntry(year: number): RealityEntry {
  return {
    calendarYear: year,
    grossIncome: 0,
    totalExpenses: 0,
    liquidAssets: 0,
    retirementAssets: 0,
    homeEquity: 0,
    notes: '',
  }
}

interface Props {
  onDataChange: (entries: RealityEntry[]) => void
}

export function RealityTracker({ onDataChange }: Props) {
  const copy = useFinanceCopy()
  const [store, setStore] = useState<RealityStore>(loadStore)
  const [editYear, setEditYear] = useState<number | null>(null)
  const [draft, setDraft] = useState<RealityEntry | null>(null)
  const [amendingYear, setAmendingYear] = useState<number | null>(null)

  function persist(updated: RealityStore) {
    saveStore(updated)
    setStore(updated)
    onDataChange(updated.entries)
  }

  function startNewEntry() {
    const existingYears = new Set(store.entries.map(e => e.calendarYear))
    const nextYear = new Date().getFullYear()
    const year = existingYears.has(nextYear) ? nextYear + 1 : nextYear
    setEditYear(year)
    setDraft(blankEntry(year))
    setAmendingYear(null)
  }

  function startAmend(entry: RealityEntry) {
    setAmendingYear(entry.calendarYear)
    setEditYear(entry.calendarYear)
    setDraft({ ...entry })
  }

  function cancelEdit() {
    setEditYear(null)
    setDraft(null)
    setAmendingYear(null)
  }

  function saveEntry() {
    if (!draft) return

    const existingYears = new Set(store.entries.map(e => e.calendarYear))

    if (amendingYear !== null) {
      // Amend: replace the existing entry
      const updated = store.entries.map(e =>
        e.calendarYear === amendingYear ? draft : e
      )
      persist({ ...store, entries: updated })
    } else {
      // Append-only: refuse if year already exists
      if (existingYears.has(draft.calendarYear)) {
        alert(
          `Year ${draft.calendarYear} already has an entry. Use "Amend" to modify it.`
        )
        return
      }
      const sorted = [...store.entries, draft].sort(
        (a, b) => a.calendarYear - b.calendarYear
      )
      persist({ ...store, entries: sorted })
    }

    cancelEdit()
  }

  function setField<K extends keyof RealityEntry>(key: K, value: RealityEntry[K]) {
    if (!draft) return
    setDraft({ ...draft, [key]: value })
  }

  const fmt = (v: number) =>
    v >= 1_000_000
      ? `$${(v / 1_000_000).toFixed(2)}M`
      : v >= 1_000
      ? `$${Math.round(v / 1_000)}k`
      : `$${Math.round(v)}`

  return (
    <div className="reality-tracker">
      <div className="reality-header">
        <h3>{copy.realityTitle}</h3>
        <button onClick={startNewEntry} className="btn-secondary">
          {copy.realityAddYearBtn}
        </button>
      </div>

      <p className="reality-subtitle">{copy.realitySubtitle}</p>

      {store.entries.length === 0 && (
        <p className="reality-empty">{copy.realityEmpty}</p>
      )}

      <table className="reality-table">
        <thead>
          <tr>
            <th>{copy.realityColYear}</th>
            <th>{copy.realityColIncome}</th>
            <th>{copy.realityColExpenses}</th>
            <th>{copy.realityColLiquid}</th>
            <th>{copy.realityColRetirement}</th>
            <th>{copy.realityColHomeEquity}</th>
            <th>{copy.realityColNetWorth}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {store.entries.map(entry => (
            <tr key={entry.calendarYear}>
              <td>{entry.calendarYear}</td>
              <td>{fmt(entry.grossIncome)}</td>
              <td>{fmt(entry.totalExpenses)}</td>
              <td>{fmt(entry.liquidAssets)}</td>
              <td>{fmt(entry.retirementAssets)}</td>
              <td>{fmt(entry.homeEquity)}</td>
              <td>
                <strong>
                  {fmt(
                    entry.liquidAssets +
                      entry.retirementAssets +
                      entry.homeEquity
                  )}
                </strong>
              </td>
              <td>
                <button className="btn-link" onClick={() => startAmend(entry)}>
                  {copy.realityAmendBtn}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editYear !== null && draft !== null && (
        <div className="entry-form">
          <h4>
            {amendingYear !== null
              ? copy.realityAmendTitle(amendingYear)
              : copy.realityNewEntryTitle(editYear)}
          </h4>
          <div className="form-grid">
            <NumberField label={copy.realityFieldYear} value={draft.calendarYear} onChange={v => setField('calendarYear', v)} />
            <NumberField label={copy.realityFieldIncome} value={draft.grossIncome} onChange={v => setField('grossIncome', v)} />
            <NumberField label={copy.realityFieldExpenses} value={draft.totalExpenses} onChange={v => setField('totalExpenses', v)} />
            <NumberField label={copy.realityFieldLiquid} value={draft.liquidAssets} onChange={v => setField('liquidAssets', v)} />
            <NumberField label={copy.realityFieldRetirement} value={draft.retirementAssets} onChange={v => setField('retirementAssets', v)} />
            <NumberField label={copy.realityFieldHomeEquity} value={draft.homeEquity} onChange={v => setField('homeEquity', v)} />
            <label className="form-label full-width">
              <span>{copy.realityFieldNotes}</span>
              <input
                type="text"
                value={draft.notes}
                onChange={e => setField('notes', e.target.value)}
                placeholder={copy.realityNotesPlaceholder}
              />
            </label>
          </div>
          <div className="form-actions">
            <button onClick={saveEntry} className="run-btn">
              {amendingYear !== null ? copy.realitySaveAmend : copy.realitySaveEntry}
            </button>
            <button onClick={cancelEdit} className="btn-secondary">
              {copy.realityCancelBtn}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) {
  return (
    <label className="form-label">
      <span>{label}</span>
      <input
        type="number"
        value={value}
        onChange={e => onChange(Number(e.target.value))}
      />
    </label>
  )
}
